export interface Product {
    _id?: string;
    product_id?: string | null;
    product_name: string;
    model?: string;
    sku?: string;
    upc?: string;
    ean?: string;
    jan?: string;
    isbn?: string;
    mpn?: string;
    location?: string;
    quantity?: number;
    stock_status?: string;
    main_image?: string;
    manufacturer_name?: string | null;
    price_mkd?: number;
    price_rsd?: number;
    price_eur?: number;
    currency?: string;
    type?: "single" | "bundle";
    bundleComponents?: Array<{
        productId: string;
        productName: string;
        quantity: number;
        price: number;
    }>;
    calculatedPrice?: number;
    salePrice?: number;
    estimatedStock?: number; // For bundle products - actual available stock based on component availability
    tax_class_id?: number;
    date_available?: string | null;
    weight?: number;
    date_added?: Date | string;
    date_modified?: Date | string;
    category_names?: string;
    additional_images?: string;
    attributes?: string;
    translations?: {
        [country: string]: {
            product_name?: string;
            description?: string;
            benefits?: string;
            ingredients?: string;
            how_to_use?: string;
            usage_1_week?: string;
            usage_1_month?: string;
            usage_3_months?: string;
            faqs?: string;
            values?: string;
            category_names?: string;
            attributes?: string;
            updatedAt?: Date;
        };
    };
    // Website content fields (base/default)
    description?: string;
    benefits?: string;
    ingredients?: string;
    how_to_use?: string;
    usage_1_week?: string;
    usage_1_month?: string;
    usage_3_months?: string;
    faqs?: string;
    values?: string;
    countryPrices?: {
        [country: string]: {
            price: number;
            currency: string;
            updatedAt?: Date;
        };
    };
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProductTranslation {
    product_name?: string;
    description?: string;
    details?: string;
    category_names?: string;
    attributes?: string;
}

export interface CountryPrice {
    price: number;
    currency: string;
}

export interface FilterState {
    searchTerm: string;
    selectedCategory: string;
    selectedStockStatus: string;
    selectedManufacturer: string;
}

export type TabType = "products" | "quantity" | "history" | "offers";

export const IMAGE_BASE_URL = 'https://naturatherapy.mk/image/cache/';

// Product Opportunity Types
export type OpportunityType = "buy_x_get_y" | "buy_x_get_free" | "gift" | "bundle";

export interface GiftItem {
  productId: string;
  productName: string;
  quantity: number;
  sku?: string;
}

export interface ProductOpportunity {
  _id?: string;
  baseProductId: string; // Reference to base product
  baseProductName: string;
  opportunityType: OpportunityType;
  name: string; // e.g., "2+2 + gift Zinc"
  description?: string;
  
  // Buy X Get Y rules
  buyQuantity?: number; // Buy X
  getQuantity?: number; // Get Y (free)
  
  // Gift items
  giftItems?: GiftItem[];
  
  // Pricing
  priceMultiplier?: number; // e.g., 2.0 for 2+2 deals
  fixedPrice?: number; // Override price if set
  discountPercentage?: number;
  
  // Variant details
  variantSuffix?: string; // e.g., "(2+2) + gift Zinc"
  variantSKU?: string; // Custom SKU for variant
  variantQuantity?: number; // Total quantity in variant
  
  // Status
  enabled: boolean;
  startDate?: Date | string;
  endDate?: Date | string;
  
  // Metadata
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface ProductVariant {
  _id?: string;
  baseProductId: string; // Reference to base product
  opportunityId: string; // Reference to opportunity that generated this variant
  variantName: string; // e.g., "Broncho Protect (2+2) + gift Zinc"
  variantSKU?: string;
  
  // Base product info (snapshot)
  baseProductName: string;
  main_image?: string;
  category_names?: string;
  manufacturer_name?: string;
  
  // Variant-specific details
  quantity: number; // Total quantity in this variant
  price: number; // Calculated price for this variant
  price_rsd?: number;
  price_mkd?: number;
  price_eur?: number;
  currency: string;
  type?: "single" | "bundle";
  bundleComponents?: Array<{
    productId: string;
    productName: string;
    quantity: number;
    price: number;
  }>;
  calculatedPrice?: number;
  salePrice?: number;
  estimatedStock?: number; // For bundle products - actual available stock based on component availability
  
  // Gift items included
  giftItems?: Array<{
    productId: string;
    productName: string;
    quantity: number;
    sku?: string;
  }>;
  
  // Opportunity details
  opportunityType: OpportunityType;
  buyQuantity?: number;
  getQuantity?: number;
  
  // Country-specific pricing
  countryPrices?: {
    [country: string]: {
      price: number;
      currency: string;
      updatedAt?: Date;
    };
  };
  
  // Translations (inherited from base product but can be customized)
  translations?: {
    [country: string]: {
      product_name?: string;
      description?: string;
      enabled?: boolean;
      updatedAt?: Date;
    };
  };
  
  // Status
  enabled: boolean;
  stock_status?: string;
  
  // Metadata
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

